import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'react-hot-toast';
import { AuthContext } from '../App';
import { Save, Eye, EyeOff, Image, Video, Layout } from 'lucide-react';

const ConfigAnuncioLateral = () => {
  const { API } = useContext(AuthContext);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(true);
  
  const [config, setConfig] = useState({
    activo: false,
    titulo: '',
    titulo_tamano: 'grande',
    titulo_color: '#1e40af',
    titulo_fuente: 'Poppins',
    descripcion: '',
    descripcion_tamano: 'mediano',
    descripcion_color: '#374151',
    fondo_color: '#f0f9ff',
    tipo_contenido: 'imagen',
    url_imagen: '',
    url_youtube: '',
    boton_activo: false,
    boton_texto: '',
    boton_link: '',
    boton_color: '#2563eb'
  });

  useEffect(() => {
    cargarConfiguracion();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      const response = await axios.get(`${API}/admin/anuncio-lateral-consultas`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error cargando configuración:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setCargando(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const guardarConfiguracion = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/admin/anuncio-lateral-consultas`, config);
      toast.success('✅ Anuncio guardado correctamente');
    } catch (error) {
      console.error('Error guardando:', error);
      toast.error('Error al guardar anuncio');
    } finally {
      setGuardando(false);
    }
  };

  // Mapeos de estilos
  const tamanosTitulo = {
    'pequeño': 'text-xl',
    'mediano': 'text-2xl',
    'grande': 'text-3xl',
    'extra-grande': 'text-4xl'
  };

  const tamanosDescripcion = {
    'pequeño': 'text-sm',
    'mediano': 'text-base',
    'grande': 'text-lg'
  };

  const fuentes = {
    'Poppins': 'font-sans',
    'Montserrat': 'font-sans',
    'Playfair': 'font-serif',
    'Roboto': 'font-sans'
  };

  if (cargando) {
    return (
      <div className="flex justify-center items-center h-64">
        <i className="fas fa-spinner fa-spin text-4xl text-blue-500"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            <Layout className="inline mr-2 text-purple-600" size={32} />
            Anuncio Lateral - Formulario de Consultas
          </h1>
          <p className="text-gray-600 mt-2">
            Configura el anuncio que aparece al lado del formulario (tipo afiche)
          </p>
        </div>
        
        <Button
          onClick={guardarConfiguracion}
          disabled={guardando}
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          {guardando ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Guardar Anuncio
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PANEL DE CONFIGURACIÓN */}
        <div className="space-y-6">
          {/* Activar/Desactivar */}
          <Card>
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Estado del Anuncio</h2>
                <Button
                  onClick={() => handleChange('activo', !config.activo)}
                  variant={config.activo ? 'default' : 'outline'}
                  className={config.activo ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  {config.activo ? (
                    <>
                      <Eye className="mr-2" size={16} />
                      Visible
                    </>
                  ) : (
                    <>
                      <EyeOff className="mr-2" size={16} />
                      Oculto
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* TÍTULO */}
          <Card>
            <CardHeader className="bg-blue-50">
              <h2 className="text-lg font-bold">📝 Título del Anuncio</h2>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Texto del Título</label>
                <Input
                  value={config.titulo}
                  onChange={(e) => handleChange('titulo', e.target.value)}
                  placeholder="Ej: ¡Inscripciones Abiertas 2025!"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-2">Tamaño</label>
                  <select
                    value={config.titulo_tamano}
                    onChange={(e) => handleChange('titulo_tamano', e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="pequeño">Pequeño</option>
                    <option value="mediano">Mediano</option>
                    <option value="grande">Grande</option>
                    <option value="extra-grande">Extra Grande</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Fuente</label>
                  <select
                    value={config.titulo_fuente}
                    onChange={(e) => handleChange('titulo_fuente', e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="Poppins">Poppins (Moderna)</option>
                    <option value="Montserrat">Montserrat (Elegante)</option>
                    <option value="Playfair">Playfair (Clásica)</option>
                    <option value="Roboto">Roboto (Simple)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Color del Título</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.titulo_color}
                    onChange={(e) => handleChange('titulo_color', e.target.value)}
                    className="h-10 w-20 border rounded cursor-pointer"
                  />
                  <Input
                    value={config.titulo_color}
                    onChange={(e) => handleChange('titulo_color', e.target.value)}
                    placeholder="#000000"
                    className="flex-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DESCRIPCIÓN */}
          <Card>
            <CardHeader className="bg-green-50">
              <h2 className="text-lg font-bold">📄 Descripción</h2>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Texto de Descripción</label>
                <Textarea
                  value={config.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="Ej: Únete a nuestra próxima generación de líderes. Becas disponibles."
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-2">Tamaño</label>
                  <select
                    value={config.descripcion_tamano}
                    onChange={(e) => handleChange('descripcion_tamano', e.target.value)}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="pequeño">Pequeño</option>
                    <option value="mediano">Mediano</option>
                    <option value="grande">Grande</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2">Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config.descripcion_color}
                      onChange={(e) => handleChange('descripcion_color', e.target.value)}
                      className="h-10 w-20 border rounded cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* DISEÑO Y MULTIMEDIA */}
          <Card>
            <CardHeader className="bg-orange-50">
              <h2 className="text-lg font-bold">🎨 Diseño y Contenido</h2>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">Color de Fondo</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={config.fondo_color}
                    onChange={(e) => handleChange('fondo_color', e.target.value)}
                    className="h-10 w-20 border rounded cursor-pointer"
                  />
                  <Input
                    value={config.fondo_color}
                    onChange={(e) => handleChange('fondo_color', e.target.value)}
                    placeholder="#f0f9ff"
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Tipo de Contenido</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => handleChange('tipo_contenido', 'imagen')}
                    className={`p-4 border-2 rounded-lg ${
                      config.tipo_contenido === 'imagen'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Image className="mx-auto mb-2" size={24} />
                    <span className="text-sm font-semibold">Imagen</span>
                  </button>
                  <button
                    onClick={() => handleChange('tipo_contenido', 'video')}
                    className={`p-4 border-2 rounded-lg ${
                      config.tipo_contenido === 'video'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200'
                    }`}
                  >
                    <Video className="mx-auto mb-2" size={24} />
                    <span className="text-sm font-semibold">Video</span>
                  </button>
                </div>
              </div>

              {config.tipo_contenido === 'imagen' && (
                <div>
                  <label className="block text-sm font-semibold mb-2">URL de Imagen</label>
                  <Input
                    value={config.url_imagen}
                    onChange={(e) => handleChange('url_imagen', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              )}

              {config.tipo_contenido === 'video' && (
                <div>
                  <label className="block text-sm font-semibold mb-2">URL de YouTube</label>
                  <Input
                    value={config.url_youtube}
                    onChange={(e) => handleChange('url_youtube', e.target.value)}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Copia el enlace completo de YouTube
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* BOTÓN DE ACCIÓN */}
          <Card>
            <CardHeader className="bg-purple-50">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">🔘 Botón de Acción</h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleChange('boton_activo', !config.boton_activo)}
                >
                  {config.boton_activo ? 'Activado' : 'Desactivado'}
                </Button>
              </div>
            </CardHeader>
            {config.boton_activo && (
              <CardContent className="p-4 space-y-3">
                <div>
                  <label className="block text-sm font-semibold mb-2">Texto del Botón</label>
                  <Input
                    value={config.boton_texto}
                    onChange={(e) => handleChange('boton_texto', e.target.value)}
                    placeholder="Ej: Inscríbete Ahora"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Link del Botón</label>
                  <Input
                    value={config.boton_link}
                    onChange={(e) => handleChange('boton_link', e.target.value)}
                    placeholder="https://ibaconnect.info"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Color del Botón</label>
                  <input
                    type="color"
                    value={config.boton_color}
                    onChange={(e) => handleChange('boton_color', e.target.value)}
                    className="h-10 w-full border rounded cursor-pointer"
                  />
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* PANEL DE PREVIEW */}
        <div className="lg:sticky lg:top-4 h-fit">
          <Card>
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
              <h2 className="text-lg font-bold">👁️ Vista Previa</h2>
              <p className="text-sm text-gray-600">Así se verá en la landing page</p>
            </CardHeader>
            <CardContent className="p-0">
              <div
                className="p-6 min-h-96"
                style={{ backgroundColor: config.fondo_color }}
              >
                {config.tipo_contenido === 'video' && config.url_youtube && (
                  <div className="mb-4 rounded-lg overflow-hidden">
                    <iframe
                      width="100%"
                      height="200"
                      src={`https://www.youtube.com/embed/${config.url_youtube.split('v=')[1]?.split('&')[0] || ''}`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                )}

                {config.tipo_contenido === 'imagen' && config.url_imagen && (
                  <div className="mb-4">
                    <img
                      src={config.url_imagen}
                      alt="Anuncio"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}

                {config.titulo && (
                  <h2
                    className={`${tamanosTitulo[config.titulo_tamano]} ${fuentes[config.titulo_fuente]} font-bold mb-3`}
                    style={{ color: config.titulo_color }}
                  >
                    {config.titulo}
                  </h2>
                )}

                {config.descripcion && (
                  <p
                    className={`${tamanosDescripcion[config.descripcion_tamano]} mb-4 whitespace-pre-wrap`}
                    style={{ color: config.descripcion_color }}
                  >
                    {config.descripcion}
                  </p>
                )}

                {config.boton_activo && config.boton_texto && (
                  <button
                    className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                    style={{ backgroundColor: config.boton_color }}
                  >
                    {config.boton_texto}
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Botón Guardar al final */}
      <div className="flex justify-end">
        <Button
          onClick={guardarConfiguracion}
          disabled={guardando}
          size="lg"
          className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          {guardando ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Guardando...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Guardar y Publicar
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ConfigAnuncioLateral;
